import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useSetAtom } from "jotai";
import { ArrowLeft, ArrowRight, X, Trash2 } from "lucide-react";

import { getText } from "shared/lib";
import { shouldShowValidationVar } from "./lib";
import {
  TreePropertyType,
  type CircumferenceTreePropertyDescriptor,
  type DBHTreePropertyDescriptor,
  type DistanceTreePropertyDescriptor,
  type TreeDescriptor,
  type TrunkTypeTreePropertyDescriptor, treeName,
} from "../lib";

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Separator,
} from "shared/components";
import { FieldSet } from "./FieldSet";
import { SpeciesField } from "./SpeciesField";
import { TrunkTypeField } from "./TrunkTypeField";
import { TreeDiagram } from "./treeDiagram";
import { MultiStemTreeDiagram } from "./multiStemTreeDiagram";

export interface TreeDialogProps {
  children?: ReactNode;
  nextDisabled?: boolean;
  onChangeTreeDescriptor?: (treeDescriptor: TreeDescriptor) => void;
  onClickDelete?: (treeDescriptor: TreeDescriptor) => void;
  onClickNext?: (treeDescriptor: TreeDescriptor) => void;
  onClickPrev?: (treeDescriptor: TreeDescriptor) => void;
  onClickSubmit?: (treeDescriptor: TreeDescriptor) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  prevDisabled?: boolean;
  subTitle?: string;
  treeDescriptor: TreeDescriptor;
  treeDescriptors: Array<TreeDescriptor>;
}

export function TreeDialog({
  children,
  onChangeTreeDescriptor,
  onClickDelete,
  onClickNext,
  onClickPrev,
  onClickSubmit,
  onOpenChange,
  open,
  subTitle,
  treeDescriptor,
  treeDescriptors,
}: TreeDialogProps) {
  const ref = useRef<HTMLDivElement>(null);

  const setShouldShowValidationVar = useSetAtom(shouldShowValidationVar);

  const checkIfValid = () => {
    const invalidLabels = Array.from(
      ref.current?.querySelectorAll("label[aria-invalid=true]") || [],
    );

    if (invalidLabels.length > 0) {
      setShouldShowValidationVar(true);
      invalidLabels[0]?.scrollIntoView({ behavior: "smooth" });
      return false;
    }

    setShouldShowValidationVar(false);
    return true;
  };

  useEffect(() => {
    setShouldShowValidationVar(false);
  }, [open]); // eslint-disable-line

  const prevTreeDescriptor = useMemo(() => {
    const index = treeDescriptors.findIndex(
      (td) => treeDescriptor?.treeId === td.treeId,
    );
    if (index > 0) {
      return treeDescriptors.at(index - 1);
    }
  }, [treeDescriptor?.treeId, treeDescriptors]);

  const nextTreeDescriptor = useMemo(() => {
    const index = treeDescriptors.findIndex(
      (td) => treeDescriptor?.treeId === td.treeId,
    );
    if (index < treeDescriptors.length - 1) {
      return treeDescriptors.at(index + 1);
    }
  }, [treeDescriptor?.treeId, treeDescriptors]);

  const trunkType = treeDescriptor.properties.get(
    TreePropertyType.trunk_type,
  ) as TrunkTypeTreePropertyDescriptor | undefined;

  const dbh = treeDescriptor.properties.get(TreePropertyType.dbh) as
    | DBHTreePropertyDescriptor
    | undefined;

  const circumference = treeDescriptor.properties.get(
    TreePropertyType.circumference,
  ) as CircumferenceTreePropertyDescriptor | undefined;

  const distance = treeDescriptor.properties.get(TreePropertyType.distance) as
    | DistanceTreePropertyDescriptor
    | undefined;

  const renderHeader = () => {
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between">
          <DialogTitle asChild>
            <h2 className="text-headingxl overflow-hidden truncate text-ellipsis">
              {treeName(treeDescriptor)}
            </h2>
          </DialogTitle>

          <div className="flex items-center gap-1">
            <Button
              className="disabled:text-textDisabled"
              disabled={!prevTreeDescriptor}
              onClick={() => {
                const valid = checkIfValid();
                if (valid && prevTreeDescriptor) {
                  onClickPrev?.(prevTreeDescriptor);
                }
              }}
              size="tight"
              variant="link"
            >
              <ArrowLeft className="min-h-5 min-w-5" />
            </Button>
            <Button
              className="disabled:text-textDisabled"
              disabled={!nextTreeDescriptor}
              onClick={() => {
                const valid = checkIfValid();
                if (valid && nextTreeDescriptor) {
                  onClickNext?.(nextTreeDescriptor);
                }
              }}
              size="tight"
              variant="link"
            >
              <ArrowRight className="min-h-5 min-w-5" />
            </Button>

            <Separator className="h-auto self-stretch" orientation="vertical" />

            <DialogClose
              className="cursor-pointer"
              onClick={() => onOpenChange?.(false)}
            >
              <X className="h-5 w-5" />
            </DialogClose>
          </div>
        </div>

        {subTitle && <p className="text-bodylg text-textSubdued">{subTitle}</p>}
      </div>
    );
  };

  const renderTreeDiagram = () => {
    let diameter = 0.1;
    let label = "";
    if (dbh?.value) {
      diameter = parseFloat(dbh.value);
      label = `${diameter.toFixed(2)}m`;
    } else if (circumference?.value) {
      const c = parseFloat(circumference.value);
      diameter = (c / 3.5) * 1.5;
      label = `${c.toFixed(2)}m`;
    }

    return (
      <div className="px-5 md:px-8">
        {trunkType?.value === "multi" ? (
          <MultiStemTreeDiagram
            label={label}
            treeDiameter={diameter}
            treeDistance={parseInt(distance?.value || "1") - 1}
          />
        ) : (
          <TreeDiagram
            label={label}
            treeDiameter={diameter}
            treeDistance={parseInt(distance?.value || "1") - 1}
          />
        )}
      </div>
    );
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideCloseButton
        className="flex max-h-[calc(100%-4rem)] flex-col gap-0 px-0 pb-0 pt-5 md:pt-8"
        aria-describedby={undefined}
      >
        <div className="flex flex-col overflow-hidden">
          <div className="flex flex-1 flex-col gap-5 overflow-auto pb-5">
            <div className="px-5 md:px-8">{renderHeader()}</div>

            {renderTreeDiagram()}

            <div className="flex flex-col gap-5 px-5 md:px-8" ref={ref}>
              <FieldSet title={getText("Type")}>
                <TrunkTypeField
                  onChange={onChangeTreeDescriptor}
                  treeDescriptor={treeDescriptor}
                />

                <SpeciesField
                  onChange={onChangeTreeDescriptor}
                  treeDescriptor={treeDescriptor}
                />
              </FieldSet>

              <Separator orientation="horizontal" />

              {children}
            </div>
          </div>
        </div>

        <div className="border-interfaceBorder bg-interfaceBgDefault flex flex-row items-center justify-between rounded-b-md border-t px-5 py-3 md:px-10 md:py-3">
          <div className="flex justify-center gap-5">
            <Button
              className="text-destructive px-0 underline"
              onClick={() => onClickDelete?.(treeDescriptor)}
              variant="link"
            >
              <Trash2 /> {getText("Delete")}
            </Button>
          </div>

          <Button
            onClick={() => {
              const valid = checkIfValid();
              if (valid) {
                onClickSubmit?.(treeDescriptor);
              }
            }}
          >
            {getText("Save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
